import { hasRight } from "@/components/crm/crmShared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { guardedSave, useLeaveGuard } from "@/lib/leaveRegistry";
import { toast } from "@/lib/toast";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Bell, LockIcon as LockIcon, Mail, RefreshCw, Save, Send, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import RecipientsLine from "@/components/settings/RecipientsLine";
import { useAuth } from "@/context/AuthContext";
import { DSelect } from "@/components/common/DeskSelect";

/**
 * EmailPrefsSection — Paramètres → Notifications email.
 *
 * Spec 2026-07-20 (fix #5) : le Premier Admin décide pour toute
 * l'organisation quels e-mails automatiques sont envoyés. L'OTP est
 * verrouillé (toujours actif) car c'est le seul vecteur d'authentification.
 * La désactivation n'affecte que l'envoi email — les notifications in-app
 * (cloche) sont toujours produites.
 *
 * Spec iter86 (Feb 2026) — refonte visuelle :
 *   • Compteur circulaire de quota Resend (48 px) dans le header.
 *   • Bordure gauche colorée par module sur chaque ligne.
 *   • Compteur "bloqués" remplacé par un badge "échecs" contextualisé
 *     (rouge si échecs > envoyés, orange si <, gris si 0).
 *   • Compteurs mensuels (reset le 1er du mois — spec point 2).
 */
const CATEGORY_LABELS = {
  auth: "Authentification",
  users: "Utilisateurs",
  tasks: "Tâches",
  documents: "Documents",
  avi: "AVI",
  crm: "Commercial",
  system: "Système",
};

// Spec iter86 : catégorie ``avi`` désormais explicitement listée pour
// exposer les 3 déclencheurs AVI qui étaient masqués (fix Phase 2).
const CATEGORY_ORDER = ["auth", "users", "tasks", "documents", "avi", "crm", "system"];

// Couleurs des modules — cohérentes avec le reste de l'app.
//   Auth      : bleu Microsoft #0078D4
//   Users     : violet #8B5CF6
//   Tasks     : jaune #F9BC26 (mêmes teintes que l'icône Tâches)
//   Documents : bleu ciel #5aa8e0 (accent Documents partout ailleurs)
//   AVI       : violet MS Teams #7b7fd4 (accent AVI partout ailleurs · spec iter87)
//   System    : gris neutre #6b7280
const CATEGORY_ACCENT = {
  auth: "#0078D4",
  users: "#8B5CF6",
  tasks: "#F9BC26",
  documents: "#5aa8e0",
  avi: "#7b7fd4",
  crm: "#7F77DD",
  system: "#6b7280",
};

const MONTH_LABEL_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

function _formatCurrentMonth(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${MONTH_LABEL_FR[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  } catch { return ""; }
}

function _fmtInt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("fr-FR");
}

/**
 * QuotaDonut — compteur circulaire du quota Resend mensuel (48 px).
 * Affiche "used / limit" au centre du cercle et le plan en légende.
 */
function QuotaDonut({ used, limit, plan, size = 48, resetDay = 1 }) {
  const pct = limit > 0 ? Math.max(0, Math.min(100, Math.round((used / limit) * 100))) : 0;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  // Couleur : vert si <60 %, orange si <90 %, rouge si ≥ 90 %.
  const color = pct >= 90 ? "#EF4444" : pct >= 60 ? "#F97316" : "#22C55E";
  // Spec iter255 (2026-03-01) — la date de renouvellement est calculée
  // depuis ``resetDay`` (jour du cycle de facturation Resend, 1..28) au
  // lieu du 1er en dur. Le cycle Resend n'est PAS calendaire mais fixé à
  // la date de souscription du plan Pro. Backend expose ce jour via
  // l'env var ``RESEND_MONTHLY_RESET_DAY``.
  const nextReset = (() => {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth(); // 0-based
    const d = now.getUTCDate();
    const target = d < resetDay
      ? new Date(Date.UTC(y, m, resetDay))
      : new Date(Date.UTC(y, m + 1, resetDay));
    const dd = String(target.getUTCDate()).padStart(2, "0");
    const mm = String(target.getUTCMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${target.getUTCFullYear()}`;
  })();
  return (
    <div className="flex items-center gap-3" data-testid="email-prefs-quota-donut">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#26262A" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={color} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums" style={{ color }}>
          {pct}%
        </div>
      </div>
      <div className="text-xs leading-tight" data-testid="email-prefs-quota-label">
        <div className="tabular-nums font-semibold" style={{ color: "#EDEDED" }}>
          {_fmtInt(used)} / {_fmtInt(limit)}
        </div>
        <div className="text-[10.5px] text-muted-foreground">{plan || "Resend"}</div>
        <div
          className="text-[10.5px] mt-0.5"
          style={{ color: "#9CA3AF" }}
          data-testid="email-prefs-quota-reset"
          title={`Le quota Resend est réinitialisé le 1er de chaque mois à 00:00 UTC. Prochaine réinitialisation : ${nextReset}.`}
        >
          Réinitialisation le {nextReset}
        </div>
      </div>
    </div>
  );
}

/**
 * Spec iter271 — « M'envoyer un aperçu » : envoi immédiat du digest
 * quotidien Documents vers l'email de l'admin connecté, pour
 * vérifier le rendu sans attendre le créneau de 08h00.
 */
function DigestPreviewButtons() {
  const [sending, setSending] = useState(null);
  const send = async (kind) => {
    setSending(kind);
    try {
      const { data } = await api.post(`/admin/email-preview-v3/digest-test?kind=${kind}`);
      const label = "Documents";
      if (data.sent) {
        toast.success(`Aperçu digest ${label} envoyé à ${data.to}`, {
          description: data.sample
            ? `Aucun élément en attente pour vous — jeu d'exemple (${data.items} lignes).`
            : `${data.items} élément${data.items > 1 ? "s" : ""} en attente.`,
        });
      } else {
        toast.error("Envoi non effectué", { description: "Déclencheur désactivé ou bloqué" });
      }
    } catch (err) {
      toast.error("Envoi impossible", { description: err.response?.data?.detail || err.message || "" });
    } finally {
      setSending(null);
    }
  };
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="digest-preview-buttons">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">M'envoyer un aperçu</span>
      {[["documents", "Documents", "#3568B0"]].map(([kind, label, color]) => (
        <button
          key={kind}
          type="button"
          onClick={() => send(kind)}
          disabled={!!sending}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border transition-colors hover:bg-white/5 disabled:opacity-50"
          style={{ color, borderColor: `${color}66` }}
          data-testid={`digest-preview-send-${kind}`}
          title={`Envoyer maintenant le digest ${label} à mon adresse`}
        >
          <Send size={11} /> {sending === kind ? "Envoi…" : label}
        </button>
      ))}
    </div>
  );
}

/**
 * Spec iter272 — heure d'envoi du digest quotidien Documents (07h/08h/09h
 * Alger). Lecture : admins ; modification : Premier Admin uniquement.
 */
function DocDigestHourSelect({ canEdit }) {
  const [hour, setHour] = useState(null);
  const [allowed, setAllowed] = useState([7, 8, 9]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/doc-digest")
      .then(({ data }) => { setHour(data.hour); setAllowed(data.allowed_hours || [7, 8, 9]); })
      .catch(() => {});
  }, []);
  const change = async (h) => {
    const next = Number(h);
    setBusy(true);
    try {
      const { data } = await api.put("/settings/doc-digest", { hour: next });
      setHour(data.hour);
      toast.success(`Digest Documents programmé à ${String(data.hour).padStart(2, "0")}h00 (Alger)`);
    } catch (err) {
      toast.error("Modification impossible", { description: err.response?.data?.detail || err.message || "" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="inline-flex items-center gap-1.5" data-testid="doc-digest-hour">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Heure digest</span>
      <DSelect
        value={hour ?? ""}
        onChange={(e) => change(e.target.value)}
        disabled={!canEdit || busy || hour === null}
        className="h-7 rounded border border-vertec-borderDark bg-[#232323] px-2 text-[11px] text-[#eeeeee] disabled:opacity-60"
        data-testid="doc-digest-hour-select"
        title={canEdit ? "Heure d'envoi du digest Documents (dim→jeu, heure d'Alger)" : "Seul le Premier Admin peut modifier l'heure"}
      >
        {hour === null && <option value="">…</option>}
        {allowed.map((h) => (
          <option key={h} value={h}>{`${String(h).padStart(2, "0")}h00`}</option>
        ))}
      </DSelect>
    </label>
  );
}

export default function EmailPrefsSection() {
  const { user } = useAuth();
  // Point 2 (Sept 2026) — destinataires paramétrables des notifications système.
  const [recip, setRecip] = useState({ keys: [], recipients: {}, editable: false });
  const [allUsers, setAllUsers] = useState([]);
  const loadRecip = useCallback(() => {
    api.get("/settings/system-recipients").then((r) => setRecip(r.data)).catch(() => {});
    api.get("/users").then((r) => setAllUsers(r.data.items || r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadRecip(); }, [loadRecip]);
  const canEdit = hasRight(user, "settings.email_notifications"); // iter203 — droit de la matrice (plus de premier-admin en dur)

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  // Spec iter91 (Feb 2026) — délais personnalisés {key: {enabled, days}}
  const [delays, setDelays] = useState({});
  const [initialDelays, setInitialDelays] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [stats, setStats] = useState({});
  const [monthStart, setMonthStart] = useState(null);
  const [resendQuota, setResendQuota] = useState(null);
  // Spec iter158 — compteurs 7 jours par expéditeur (otp / technique /
  // notifications) pour le widget dédié en tête de section.
  const [senderStats7d, setSenderStats7d] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/email-prefs");
      setCatalog(data.catalog || []);
      setValues(data.values || {});
      setInitialValues(data.values || {});
      setDelays(data.delays || {});
      setInitialDelays(data.delays || {});
      setUpdatedAt(data.updated_at || null);
      setStats(data.stats || {});
      setMonthStart(data.month_start || data.week_start || null);
      setResendQuota(data.resend_quota || null);
      setSenderStats7d(data.sender_stats_7d || null);
    } catch (err) {
      toast.error("Impossible de charger les préférences", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    const vDirty = Object.keys(values).some((k) => values[k] !== initialValues[k]);
    const dDirty = catalog.some((r) => {
      const cur = delays[r.key] || { enabled: false, days: 0 };
      const ini = initialDelays[r.key] || { enabled: false, days: 0 };
      return cur.enabled !== ini.enabled || Number(cur.days || 0) !== Number(ini.days || 0);
    });
    return vDirty || dDirty;
  }, [values, initialValues, delays, initialDelays, catalog]);

  const toggle = (key, next) => {
    if (!canEdit) return;
    setValues((v) => ({ ...v, [key]: next }));
  };

  const setDelayEnabled = (key, next) => {
    if (!canEdit) return;
    setDelays((d) => ({ ...d, [key]: { ...(d[key] || { days: 0 }), enabled: !!next } }));
  };
  const setDelayDays = (key, days) => {
    if (!canEdit) return;
    const clean = Math.max(0, Math.min(365, Number(days) || 0));
    setDelays((d) => ({ ...d, [key]: { ...(d[key] || { enabled: true }), days: clean } }));
  };

  useLeaveGuard({ dirty: !!dirty, label: "Paramètres · E-mails", module: "parametres", save: guardedSave(() => save()) }); // eslint-disable-line no-use-before-define
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/email-prefs", { values, delays });
      setValues(data.values || {});
      setInitialValues(data.values || {});
      setDelays(data.delays || {});
      setInitialDelays(data.delays || {});
      setUpdatedAt(data.updated_at || null);
      setStats(data.stats || {});
      setMonthStart(data.month_start || data.week_start || null);
      setResendQuota(data.resend_quota || null);
      setSenderStats7d(data.sender_stats_7d || null);
      toast.success("Préférences enregistrées");
    } catch (err) {
      toast.error("Enregistrement impossible", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const buckets = {};
    catalog.forEach((row) => {
      const c = row.category || "other";
      if (!buckets[c]) buckets[c] = [];
      buckets[c].push(row);
    });
    return buckets;
  }, [catalog]);

  const activeCount = useMemo(() => {
    return catalog.filter((r) => r.always_on || values[r.key]).length;
  }, [catalog, values]);

  const monthLabel = _formatCurrentMonth(monthStart);
  // Spec iter255 (2026-03-01) — le backend expose maintenant
  // ``monthly_sent`` (vérité brute du header ``x-resend-monthly-quota``).
  // Fallback : ``monthly_limit - monthly_remaining`` pour absorber un
  // backend antérieur qui n'expose pas encore ``monthly_sent``.
  const quotaUsed = (() => {
    if (!resendQuota) return null;
    if (typeof resendQuota.monthly_sent === "number") return resendQuota.monthly_sent;
    if (resendQuota.monthly_limit != null && resendQuota.monthly_remaining != null) {
      return Math.max(0, resendQuota.monthly_limit - resendQuota.monthly_remaining);
    }
    return null;
  })();

  if (loading) {
    return (
      <Card className="bg-card border-vertec-borderDark p-8">
        <div className="text-sm text-muted-foreground">Chargement des préférences…</div>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-vertec-borderDark p-8" data-testid="email-prefs-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className="rounded-lg flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, background: "#141417", border: "1px solid #26262C", color: "#0078D4" }}
          >
            <Mail size={22} />
          </div>
          <div>
            <h2 className="text-2xl font-semibold mb-1">Notifications email</h2>
            <p className="text-muted-foreground text-sm max-w-2xl">
              Contrôlez les emails automatiques envoyés par <strong>Le Desk</strong>. La
              désactivation d&apos;un déclencheur n&apos;affecte que l&apos;envoi email — les
              notifications in-app (cloche 🔔) restent toujours produites.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {resendQuota && quotaUsed !== null && (
            <QuotaDonut
              used={quotaUsed}
              limit={resendQuota.monthly_limit || 0}
              plan={resendQuota.plan}
              resetDay={resendQuota.monthly_reset_day || 1}
            />
          )}
          <div className="flex items-center gap-3">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
              style={{
                background: "transparent",
                color: "#22C55E",
                border: "1px solid rgba(34,197,94,0.45)",
              }}
              data-testid="email-prefs-active-count"
            >
              <ShieldCheck size={13} />
              <span>{activeCount} / {catalog.length} actifs</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={saving}
              className="gap-1.5 border-vertec-borderDark"
              data-testid="email-prefs-reload"
            >
              <RefreshCw size={13} /> Actualiser
            </Button>
            <DocDigestHourSelect canEdit={canEdit} />
            <DigestPreviewButtons />
          </div>
        </div>
      </div>

      {/* Spec iter158 (Feb 2026) — Widget « Envois par expéditeur (7 j) »
          Répartition des envois RÉUSSIS des 7 derniers jours par sender
          (otp@ / technique@ / notifications@). Aide à valider en un
          coup d'œil que le routage 3 groupes fonctionne, et à repérer
          une anomalie (ex. 0 envoi métier = incident notifications@). */}
      {senderStats7d && (
        <div
          className="rounded-lg p-4 mb-6"
          style={{ background: "#141417", border: "1px solid #26262C" }}
          data-testid="email-sender-stats-7d"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#B7B7B7" }}>
                Envois par expéditeur · 7 derniers jours
              </span>
            </div>
            <span
              className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: "transparent", border: "1px solid #33333B", color: "#B7B7B7" }}
              data-testid="email-sender-stats-total"
            >
              Total : {senderStats7d.total_sent}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { key: "otp",           color: "#3B82F6" },
              { key: "technique",     color: "#DC2626" },
              { key: "notifications", color: "#F97316" },
            ].map(({ key, color }) => {
              const s = senderStats7d.senders?.[key] || { sent: 0, email: "—", label: key };
              const pct = senderStats7d.total_sent > 0
                ? Math.round((s.sent / senderStats7d.total_sent) * 100)
                : 0;
              return (
                <div
                  key={key}
                  className="rounded-md p-3"
                  style={{ background: "#17171B", border: "1px solid #26262C", borderLeft: `3px solid ${color}` }}
                  data-testid={`email-sender-stats-${key}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <div
                      className="text-[10px] uppercase tracking-widest font-bold"
                      style={{ color }}
                    >
                      {s.label}
                    </div>
                    <div className="text-[10px]" style={{ color: "#8A8A8A" }}>
                      {pct}%
                    </div>
                  </div>
                  <div className="text-2xl font-bold leading-none" style={{ color: "#EDEDED" }}>
                    {s.sent}
                    <span className="text-xs font-normal ml-1" style={{ color: "#8A8A8A" }}>envois</span>
                  </div>
                  <div className="mt-1 text-[10.5px] font-mono truncate" style={{ color }}>
                    {s.email}
                  </div>
                  {/* Barre de progression fine */}
                  <div
                    className="mt-2 rounded-full overflow-hidden"
                    style={{ height: 4, background: "rgba(255,255,255,0.05)" }}
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Groups */}
      <div className="space-y-8">
        {CATEGORY_ORDER.filter((c) => grouped[c]).map((cat) => {
          const rows = grouped[cat];
          const accent = CATEGORY_ACCENT[cat] || "#6b7280";
          return (
            <div key={cat} data-testid={`email-prefs-category-${cat}`}>
              <div className="flex items-center gap-2 mb-1">
                <div style={{ width: 3, height: 20, background: accent, borderRadius: 2 }} />
                <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: accent }}>
                  {CATEGORY_LABELS[cat] || cat}
                </h3>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  · {rows.length} déclencheur{rows.length > 1 ? "s" : ""}
                </span>
              </div>
              {/* Mention mensuelle (spec iter86 point 2). */}
              <div
                className="text-[10.5px] text-muted-foreground italic mb-3 ml-5"
                data-testid={`email-prefs-month-${cat}`}
              >
                {monthLabel ? <><span className="capitalize">{monthLabel}</span> · reset le 1er de chaque mois</> : null}
              </div>

              <div className="space-y-2">
                {rows.map((row) => {
                  const on = row.always_on ? true : !!values[row.key];
                  const s = stats[row.key] || { sent: 0, skipped: 0, failed: 0 };
                  // Couleur du badge "échecs" selon la gravité :
                  //   • 0 échec → gris neutre
                  //   • échecs > envoyés → rouge (signal fort)
                  //   • échecs ≤ envoyés → orange
                  const failed = s.failed || 0;
                  const sent = s.sent || 0;
                  const failColor = failed === 0
                    ? { fg: "#94a3b8" }
                    : (failed > sent ? { fg: "#EF4444" } : { fg: "#F97316" });
                  return (
                    <div
                      key={row.key}
                      className="flex items-start gap-4 rounded-md p-4"
                      style={{
                        background: "#17171B",
                        border: "1px solid #26262C",
                        borderLeft: `3px solid ${accent}`,  // Spec iter86 · bord gauche coloré par module
                      }}
                      data-testid={`email-pref-row-${row.key}`}
                    >
                      <div className="pt-0.5">
                        {row.always_on ? (
                          <div
                            className="flex items-center justify-center rounded-full"
                            style={{
                              width: 32, height: 32,
                              background: "transparent",
                              color: "#0078D4",
                              border: "1px solid rgba(0,120,212,0.45)",
                            }}
                            title="Toujours actif — obligatoire pour l'authentification"
                          >
                            <LockIcon size={14} />
                          </div>
                        ) : (
                          <Switch
                            checked={on}
                            onCheckedChange={(v) => toggle(row.key, v)}
                            disabled={!canEdit || saving}
                            data-testid={`email-pref-switch-${row.key}`}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold">{row.label}</div>
                          {row.always_on && (
                            <span
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                              style={{ background: "transparent", border: "1px solid rgba(0,120,212,0.45)", color: "#0078D4" }}
                            >
                              Toujours actif
                            </span>
                          )}
                          {!row.always_on && !on && (
                            <span
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                              style={{ background: "transparent", border: "1px solid rgba(239,68,68,0.45)", color: "#EF4444" }}
                            >
                              Désactivé
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {row.description}
                        </div>
                        {recip.keys.includes(row.key) && (
                          <RecipientsLine notifKey={row.key} label={row.label} ids={recip.recipients[row.key] || []} users={allUsers} editable={recip.editable} onSaved={loadRecip} />
                        )}
                        {/* Spec iter91 (Feb 2026) — délai personnalisé.
                            Toggle OFF par défaut → comportement immédiat
                            inchangé. Toggle ON → phrase éditable inline. */}
                        {!row.always_on && on && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`email-pref-delay-${row.key}`}>
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] text-muted-foreground select-none">
                              <input
                                type="checkbox"
                                checked={!!delays[row.key]?.enabled}
                                onChange={(e) => setDelayEnabled(row.key, e.target.checked)}
                                disabled={!canEdit || saving}
                                data-testid={`email-pref-delay-toggle-${row.key}`}
                                style={{ accentColor: accent }}
                              />
                              <span>Délai personnalisé</span>
                            </label>
                            {delays[row.key]?.enabled && (
                              <span className="text-[11.5px] text-white/80 inline-flex items-center gap-1.5">
                                Alerter
                                <input
                                  type="number"
                                  min={0}
                                  max={365}
                                  value={delays[row.key]?.days ?? 0}
                                  onChange={(e) => setDelayDays(row.key, e.target.value)}
                                  disabled={!canEdit || saving}
                                  className="w-14 h-6 rounded px-1.5 text-center tabular-nums"
                                  style={{
                                    background: "#141417",
                                    border: `1px solid ${accent}55`,
                                    color: "#EDEDED",
                                  }}
                                  data-testid={`email-pref-delay-days-${row.key}`}
                                />
                                {row.delay_label ? row.delay_label : `jour${(delays[row.key]?.days ?? 0) > 1 ? "s" : ""} après la date limite`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Spec iter86 — badges façon notification :
                          icône d'envoi verte avec pastille, icône d'alerte
                          contextualisée. Remplace l'ancien badge "bloqués"
                          toujours à 0. */}
                      <div className="shrink-0 flex items-center gap-2" data-testid={`email-pref-stats-${row.key}`}>
                        <div
                          className="relative inline-flex items-center justify-center rounded-lg"
                          style={{
                            width: 34, height: 34,
                            background: "transparent",
                            border: "1px solid #33333B",
                            color: "#22C55E",
                          }}
                          title={`${sent} email(s) envoyé(s) ce mois-ci`}
                          data-testid={`email-pref-sent-${row.key}`}
                        >
                          <Send size={14} />
                          <span
                            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 tabular-nums"
                            style={{
                              background: "#141417", color: "#22C55E",
                              border: "1px solid rgba(34,197,94,0.5)",
                            }}
                          >
                            {sent > 999 ? "999+" : sent}
                          </span>
                        </div>
                        <div
                          className="relative inline-flex items-center justify-center rounded-lg"
                          style={{
                            width: 34, height: 34,
                            background: "transparent",
                            border: "1px solid #33333B",
                            color: failColor.fg,
                          }}
                          title={`${failed} échec(s) d'envoi ce mois-ci`}
                          data-testid={`email-pref-failed-${row.key}`}
                        >
                          <AlertTriangle size={14} />
                          <span
                            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 tabular-nums"
                            style={{
                              background: "#141417", color: failColor.fg,
                              border: `1px solid ${failColor.fg}80`,
                            }}
                          >
                            {failed > 999 ? "999+" : failed}
                          </span>
                        </div>
                      </div>
                      <div className="pt-1 shrink-0">
                        <Bell size={14} className="text-muted-foreground" title="La notification in-app reste toujours envoyée" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer / actions */}
      <div className="mt-8 pt-6 border-t border-vertec-borderDark flex items-center justify-between gap-4 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {updatedAt ? (
            <>Dernière mise à jour : {new Date(updatedAt).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</>
          ) : (
            <>Aucune modification enregistrée à ce jour — tous les emails sont actifs par défaut.</>
          )}
        </div>
        {canEdit ? (
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="gap-1.5 bg-[#0078D4] hover:bg-[#005ea6] text-white"
            data-testid="email-prefs-save"
          >
            <Save size={14} /> {saving ? "Enregistrement…" : dirty ? "Enregistrer les modifications" : "Enregistré"}
          </Button>
        ) : (
          <div className="text-xs text-muted-foreground italic">
            Seul le Premier Admin peut modifier ces préférences.
          </div>
        )}
      </div>
    </Card>
  );
}
